import { generateSecureUploadLink } from './OneDriveHelper';
import { emailTemplates } from './emailTemplates';

let googleSdkPromise = null;
let gmailAccessToken = null;
let gmailTokenExpiresAt = 0;
let gmailUserEmail = '';

const ensureGoogleSdk = async () => {
  if (typeof window === 'undefined') {
    throw new Error('Google sign-in is only available in the browser.');
  }

  if (window.google?.accounts?.oauth2) {
    return;
  }

  if (!googleSdkPromise) {
    googleSdkPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Google auth script.')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google auth script.'));
      document.head.appendChild(script);
    });
  }

  await googleSdkPromise;
};

export const authenticateWithGmail = async () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error('Missing Google client ID. Please set REACT_APP_GOOGLE_CLIENT_ID in your environment.');
  }

  await ensureGoogleSdk();

  if (gmailAccessToken && Date.now() < gmailTokenExpiresAt) {
    return { accessToken: gmailAccessToken, userEmail: gmailUserEmail };
  }

  return new Promise((resolve, reject) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/gmail.send',
      prompt: 'consent',
      callback: (response) => {
        try {
          if (response.error) {
            reject(new Error(response.error_description || response.error));
            return;
          }

          gmailAccessToken = response.access_token;
          gmailTokenExpiresAt = Date.now() + 3500 * 1000;
          gmailUserEmail = response.email || '';
          resolve({ accessToken: gmailAccessToken, userEmail: gmailUserEmail });
        } catch (error) {
          reject(error);
        }
      }
    });

    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
};

const encodeGmailMessage = (message) => {
  const utf8Bytes = new TextEncoder().encode(message);
  let binary = '';
  utf8Bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const openGmailCompose = (emailConfig) => {
  const { subject, body, to } = emailConfig;
  const composeUrl = new URL('https://mail.google.com/mail/?view=cm');
  if (to) composeUrl.searchParams.set('to', to);
  if (subject) composeUrl.searchParams.set('su', subject);
  if (body) composeUrl.searchParams.set('body', body);

  const popup = window.open(composeUrl.toString(), '_blank', 'width=980,height=760,noopener,noreferrer');
  if (!popup) {
    window.location.href = composeUrl.toString();
  }

  return { success: true, fallbackUsed: true };
};

/**
 * Sends an email using Gmail API through Google OAuth sign-in.
 */
export const sendEmailViaGmail = async (emailConfig) => {
  const { subject, body, to, attachments } = emailConfig;
  const { accessToken } = await authenticateWithGmail();

  let rawMessage;

  if (attachments && attachments.length > 0) {
    const boundary = "----=" + Date.now();
    const parts = [
      `From: me`,
      `To: ${to}`,
      `Subject: ${subject}`,
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
      '',
      `--${boundary}`,
      'Content-Type: text/html; charset=UTF-8',
      '',
      body,
      ''
    ];

    attachments.forEach(attachment => {
      parts.push(`--${boundary}`);
      parts.push(`Content-Type: ${attachment['@odata.type'] === '#microsoft.graph.fileAttachment' ? 'application/octet-stream' : 'application/octet-stream'}`);
      parts.push('Content-Transfer-Encoding: base64');
      parts.push(`Content-Disposition: attachment; filename="${attachment.name}"`);
      parts.push('');
      parts.push(attachment.contentBytes);
    });

    parts.push(`--${boundary}--`);
    rawMessage = parts.join('\r\n');
  } else {
    rawMessage = [
      `From: me`, `To: ${to}`, `Subject: ${subject}`, 'Content-Type: text/html; charset=UTF-8', '', body
    ].join('\r\n');
  }

  try {
    const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ raw: encodeGmailMessage(rawMessage) })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn('Gmail API send failed, falling back to Gmail compose.', errorData);
      return openGmailCompose(emailConfig);
    }

    return { success: true, fallbackUsed: false };
  } catch (error) {
    console.warn('Gmail API send failed, falling back to Gmail compose.', error);
    return openGmailCompose(emailConfig);
  }
};

/**
 * Sends an email using the Microsoft Graph API
 */
export const sendEmailViaGraphAPI = async (msalInstance, account, emailConfig, taxType='personal') => {
  const { subject, body, to, attachments } = emailConfig;

  try {
    // 1. Acquire an access token silently
    const tokenRequest = {
      scopes: ["Mail.Send"],
      account: account
    };
    const authResult = await msalInstance.acquireTokenSilent(tokenRequest);

    // 2. Construct the Graph API payload for an HTML email
    const mailPayload = {
      message: {
        subject: subject,
        body: {
          contentType: "HTML", // This is the magic word that fixes the formatting!
          content: body
        },
        toRecipients: [
          {
            emailAddress: {
              address: to
            }
          }
        ]
      },
      saveToSentItems: "true"
    };

    if (attachments && attachments.length > 0) {
      mailPayload.message.attachments = attachments;
    }

    if (taxType.toLowerCase() === 'cvitp') {
        mailPayload.message.from = {
            emailAddress: {
                address: "cvitp-team@orientalbiz.ca"
            }
        };
    } else if (taxType.toLowerCase() === 'fax') {
        mailPayload.message.from = {
            emailAddress: {
                address: "fax@orientalbiz.ca"
            }
        };
    }


    // 3. Make the API call
    const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authResult.accessToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(mailPayload)
    });

    if (!response.ok) {
      throw new Error(`Graph API Error: ${response.statusText}`);
    }

    return true; // Success!

  } catch (error) {
    console.error("Failed to send email via Graph API:", error);
    throw error;
  }
};

/**
 * Generates the email draft details so the agent can review them in a modal
 */
export const generateRequestDetailsDraft = async (config) => {
  const { 
    customerEmail, 
    customerData, 
    msalInstance, 
    account, 
    templateType = 'cvitp',
    action = 'requestDetails',
    customData = {}
  } = config;

  try {

    // Generate secure upload link from OneDrive
    let uploadLink;
    try {
      uploadLink = await generateSecureUploadLink(msalInstance, account, customerData, templateType === 'cvitp' ? 'CVITP' : templateType);
    } catch (error) {
      console.warn('Could not generate OneDrive link, using fallback:', error);
    }

    let template;
    let bodyTemplate;
    let subject;

    switch(action) {
        case 'requestFaxLink':
            template = emailTemplates.faxRequestLink;
            bodyTemplate = template.bodyTemplate(customData.generatedLink);
            break;
            
        case 'requestEsign':
            switch(templateType.toLowerCase()) {
                case 'personal': template = emailTemplates.personalRequestEsign; break;
                case 'corporate': template = emailTemplates.corporateRequestEsign; break;
                case 'cvitp':
                default: template = emailTemplates.cvitpRequestEsign; break;
            }
            bodyTemplate = template.bodyTemplate(customData.generatedLink, customData.taxYear, customData.clientName);
            break;
            
        case 'requestDocument':
            switch(templateType.toLowerCase()) {
                case 'personal': template = emailTemplates.personalRequestDocument; break;
                case 'corporate': template = emailTemplates.corporateRequestDocument; break;
                case 'cvitp':
                default: template = emailTemplates.cvitpRequestDocument; break;
            }
            const customerFolderName = (customerData.name || customerData.businessNumber || customerData.corporateName || 'Unknown_Customer').replace(/[\\/:*?"<>|]/g, '_');
            bodyTemplate = template.bodyTemplate(customerFolderName, uploadLink);
            break;
            
        case 'requestDetails':
        default:
            switch(templateType.toLowerCase()) {
                case 'personal': template = emailTemplates.personalRequestDetails; break;
                case 'corporate': template = emailTemplates.corporateRequestDetails; break;
                case 'cvitp':
                default: template = emailTemplates.cvitpRequestDetails; break;
            }
            bodyTemplate = template.bodyTemplate(uploadLink);
            break;
            
        case 'sendInvoice':
            // Specific dynamic template handling for invoices
            template = emailTemplates.sendInvoice;
            if (typeof template.subject === 'function') {
                subject = template.subject(customData.invoiceNum);
            }
            bodyTemplate = template.bodyTemplate(customData.invoiceNum, customData.dueDate, customData.totalAmount);
            break;
        
        case 'sendConfirmation':
            switch(templateType.toLowerCase()) {
                case 'cvitp':
                    template = emailTemplates.cvitpSendConfirmation;
                    break;
                case 'corporate':
                    template = emailTemplates.corporateFilingConfirmation;
                    break;
                case 'personal':
                default: template = emailTemplates.sendConfirmation; break;
            }
            bodyTemplate = template.bodyTemplate(customerData.name || customerData.businessName || 'Valued Client');
            break;
    }

    // Return the configuration object instead of launching an email client
    return {
      subject: subject || template.subject,
      body: bodyTemplate,
      to: customerEmail || 'magizhinitech@gmail.com'
    };

  } catch (error) {
    console.error('Error generating email draft:', error);
    throw new Error('Error generating email draft: ' + error.message);
  }
};